import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import {
  Archive,
  BookOpen,
  Building2,
  CalendarDays,
  Database,
  Download,
  FileText,
  LockKeyhole,
  Search,
  ShieldCheck,
} from 'lucide-react';
import { Skeleton, Empty } from '../ui/kit';
import './OrderLog.css';
import PageHeader from '../components/PageHeader';
import { datumUndZeit, tagSchluessel } from '../lib/zeit';

// Auftragsbuch (§ 49 Abs. 4 PBefG) — NUR LESEND, z. B. für Behörden-Anfragen.
// Einträge schreibt das Backend automatisch (Vermittlung/Annahme); Quelle = lib/orderLog,
// identisch zum Partner-Portal. Aufbewahrung mind. 1 Jahr — Löschen gibt es bewusst nicht.

const STATUS_DE = {
  searching: 'In Vermittlung',
  scheduled: 'Reserviert',
  accepted: 'Angenommen',
  arrived: 'Angekommen',
  in_progress: 'Unterwegs',
  completed: 'Abgeschlossen',
  cancelled: 'Storniert',
  no_driver: 'Kein Fahrer',
};

const fmtTs = (ts) => datumUndZeit(ts);

// ADMIN-KONTOBEWEGUNG-001 (C4): ÖRTLICHER Kalendertag, nicht der UTC-Tag.
// `toISOString().slice(0,10)` liefert zwischen 00:00 und 02:00 Berliner Zeit noch GESTERN —
// dann startet das Auftragsbuch nachts auf dem Vortag und die heutigen Einträge fehlen,
// obwohl der Server-Filter korrekt ist. `en-CA` ist die Sprachkennung, deren Datumsform
// JJJJ-MM-TT ist; gerechnet wird ausdrücklich in Europe/Berlin (lib/zeit `tagSchluessel`) — derselben
// Zeitzone, in der die Liste ihre Uhrzeiten anzeigt. Identischer Weg wie in Invoices.js.
function defaultRange() {
  const to = new Date();
  const from = new Date();
  from.setDate(to.getDate() - 30);
  const iso = (d) => tagSchluessel(d);
  return { from: iso(from), to: iso(to) };
}

const registerFacts = [
  {
    icon: LockKeyhole,
    overline: 'Registerschutz',
    title: 'Ausschließlich lesend',
    text: 'Keine Bearbeitung und keine Löschung in der Admin-Oberfläche.',
  },
  {
    icon: Database,
    overline: 'Datenherkunft',
    title: 'Automatisch protokolliert',
    text: 'Vermittlung und Annahme werden serverseitig in das Register geschrieben.',
  },
  {
    icon: Archive,
    overline: 'Aufbewahrung',
    title: 'Mindestens ein Jahr',
    text: 'Der vollständige Zeitraum bleibt für berechtigte Nachweise abrufbar.',
  },
];

export default function OrderLog() {
  const [entries, setEntries] = useState([]);
  const [range, setRange] = useState(defaultRange());
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    api.get('/admin/order-log', { params: { from: range.from, to: range.to } })
      .then(r => setEntries(r.data.entries || []))
      .catch(() => toast.error('Auftragsbuch konnte nicht geladen werden'))
      .finally(() => setLoading(false));
  }, [range]);

  useEffect(() => { load(); }, [load]);

  const exportFile = async (format) => {
    setExporting(format);
    try {
      const r = await api.get('/admin/order-log', {
        params: { from: range.from, to: range.to, format },
        responseType: 'blob',
      });
      const type = format === 'pdf' ? 'application/pdf' : 'text/csv;charset=utf-8';
      const url = window.URL.createObjectURL(new Blob([r.data], { type }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Auftragsbuch_${range.from}_${range.to}.${format}`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Export fehlgeschlagen');
    } finally {
      setExporting('');
    }
  };

  const q = search.trim().toLowerCase();
  const filtered = q
    ? entries.filter(e => [
      e.company_name,
      e.driver_name,
      e.license_plate,
      e.pickup_address,
      e.dropoff_address,
      e.ride_id,
    ].some(v => String(v || '').toLowerCase().includes(q)))
    : entries;

  return (
    <div className="order-log-page">
      <PageHeader
        title="Auftragsbuch"
        description="Nachweis der Auftragseingänge am Betriebssitz gemäß § 49 Abs. 4 PBefG."
        actions={

        <div className="order-log-heading-actions">
          <span className="order-log-readonly-pill">
            <ShieldCheck size={13} />
            Nur lesend
          </span>
          <button
            className="order-log-export is-csv"
            onClick={() => exportFile('csv')}
            disabled={!!exporting}
            aria-label="Auftragsbuch als CSV exportieren"
          >
            <Download size={14} />
            {exporting === 'csv' ? 'CSV wird erstellt …' : 'CSV exportieren'}
          </button>
          <button
            className="order-log-export is-pdf"
            onClick={() => exportFile('pdf')}
            disabled={!!exporting}
            aria-label="Auftragsbuch als PDF exportieren"
          >
            <FileText size={14} />
            {exporting === 'pdf' ? 'PDF wird erstellt …' : 'PDF exportieren'}
          </button>
        </div>
      }
      />

      <section className="order-log-facts" aria-label="Registereigenschaften">
        {registerFacts.map(({ icon: Icon, overline, title, text }) => (
          <article key={title}>
            <span className="order-log-fact-icon"><Icon size={15} /></span>
            <div>
              <small>{overline}</small>
              <strong>{title}</strong>
              <p>{text}</p>
            </div>
          </article>
        ))}
      </section>

      <section className="order-log-filter-panel">
        <header>
          <div>
            <span><CalendarDays size={15} /></span>
            <div>
              <small>Registerauszug</small>
              <strong>Zeitraum und lokale Suche</strong>
            </div>
          </div>
          <p>Der Zeitraum wird vom Server geladen; die Suche grenzt den geladenen Auszug lokal ein.</p>
        </header>

        <div className="order-log-filter-grid">
          <label className="order-log-search-field">
            <span>Suche im geladenen Auszug</span>
            <div>
              <Search size={14} />
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Partner, Fahrer, Kennzeichen, Adresse …"
              />
            </div>
          </label>
          <label>
            <span>Von</span>
            <input
              aria-label="Zeitraum von"
              type="date"
              value={range.from}
              onChange={e => setRange(r => ({ ...r, from: e.target.value }))}
            />
          </label>
          <label>
            <span>Bis</span>
            <input
              aria-label="Zeitraum bis"
              type="date"
              value={range.to}
              onChange={e => setRange(r => ({ ...r, to: e.target.value }))}
            />
          </label>
          <div className="order-log-result-count" aria-live="polite">
            <small>Gefilterter Auszug</small>
            <strong>{filtered.length}</strong>
            <span>von {entries.length} Einträgen</span>
          </div>
        </div>
      </section>

      <section className="order-log-table-card">
        <header>
          <div>
            <span className="order-log-overline">Betriebsregister</span>
            <h2>Protokollierte Auftragseingänge</h2>
          </div>
          <span><LockKeyhole size={11} /> unveränderlicher Nachweis</span>
        </header>

        {loading ? (
          <div className="order-log-loading">
            {[0, 1, 2, 3, 4, 5].map(i => <Skeleton key={i} h={42} />)}
          </div>
        ) : filtered.length === 0 ? (
          <Empty
            icon={BookOpen}
            title="Keine Einträge im gewählten Zeitraum"
            hint={q ? 'Passe die lokale Suche an oder wähle einen anderen Zeitraum.' : 'Für diesen Zeitraum enthält das Register keine Auftragseingänge.'}
          />
        ) : (
          <div className="order-log-table-scroll">
            <table className="order-log-table">
              <thead>
                <tr>
                  {[
                    'Partner',
                    'Auftragseingang',
                    'Annahme',
                    'Fahrtbeginn',
                    'Fahrtende',
                    'Abholung → Ziel',
                    'Fahrer',
                    'Fahrzeug',
                    'Status',
                  ].map(column => <th key={column}>{column}</th>)}
                </tr>
              </thead>
              <tbody>
                {filtered.map(e => (
                  <tr key={e.ride_id}>
                    <td>
                      <div className="order-log-party">
                        <span><Building2 size={12} /></span>
                        <div>
                          <strong>{e.company_name || '—'}</strong>
                          <small>{e.ride_id || 'Ohne Fahrt-ID'}</small>
                        </div>
                      </div>
                    </td>
                    <td className="order-log-time is-primary">{fmtTs(e.received_at)}</td>
                    <td className="order-log-time">{fmtTs(e.accepted_at)}</td>
                    <td className="order-log-time">{fmtTs(e.started_at)}</td>
                    <td className="order-log-time">{fmtTs(e.completed_at)}</td>
                    <td>
                      <div className="order-log-route">
                        <span><i />{e.pickup_address || '—'}</span>
                        <span><i />{e.dropoff_address || '—'}</span>
                      </div>
                    </td>
                    <td className="order-log-person">{e.driver_name || '—'}</td>
                    <td>
                      <div className="order-log-vehicle">
                        <strong>{[e.vehicle_brand, e.vehicle_model].filter(Boolean).join(' ') || '—'}</strong>
                        {e.license_plate && <small>{e.license_plate}</small>}
                      </div>
                    </td>
                    <td>
                      <span className={`order-log-status is-${e.status || 'unknown'}`}>
                        <i />
                        {STATUS_DE[e.status] || e.status || 'Unbekannt'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
